import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { partition } from "ramda";
import { Switch, Route, useLocation } from "react-router-dom";

import config from "../config";
import AppWrapper from "./AppWrapper";
import AuthenticatedOnly from "./AuthenticatedOnly";
import { StripeProvider } from "react-stripe-elements";
import { SentryErrorBoundary } from "./SentryErrorBoundary";
import { PrimaryLayout } from "./PrimaryLayout";
import { isServer, isClient } from "../global";
import {
  RouteLayout,
  RouteOptions,
  authenticatedRoutes,
  publicRoutes,
  PrimaryRouteOptions,
  SecondaryRouteOptions,
  CustomRouteOptions
} from "../routes";
import { SecondaryLayout } from "./SecondaryLayout";
import { useGetFeaturesQuery } from "../graphql/hooks/useGetFeaturesQuery";
import { useDispatch } from "react-redux";
import { featuresActions } from "../store/features";
import { rebootIntercom } from "../intercom";
import { page } from "../analytics";
import ScrollToTop from "./ScrollToTop";

const RoutesLayoutSwitch = (props: { routes: RouteOptions[] }) => {
  const primaryRoutes = props.routes.filter(
    (route: RouteOptions): route is PrimaryRouteOptions =>
      "layout" in route && route.layout === RouteLayout.Primary
  );
  const secondaryRoutes = props.routes.filter(
    (route: RouteOptions): route is SecondaryRouteOptions =>
      "layout" in route && route.layout === RouteLayout.Secondary
  );
  const customRoutes = props.routes.filter(
    (route: RouteOptions): route is CustomRouteOptions => !("layout" in route)
  );

  const [narrowSecondaryRoutes, defaultSecondaryRoutes] = partition(
    route => route.layoutVariant === "narrow",
    secondaryRoutes
  );

  return (
    <Switch>
      <Route
        path={primaryRoutes.map(r => r.path)}
        exact={true}
        render={() => (
          <PrimaryLayout>
            <Switch>
              {primaryRoutes.map((r, i) => (
                <Route
                  exact={true}
                  key={i}
                  path={r.path}
                  component={r.component}
                />
              ))}
            </Switch>
          </PrimaryLayout>
        )}
      />

      <Route
        path={narrowSecondaryRoutes.map(r => r.path)}
        exact={true}
        render={() => (
          <SecondaryLayout variant="narrow">
            <Switch>
              {narrowSecondaryRoutes.map((r, i) => (
                <Route
                  exact={true}
                  key={i}
                  path={r.path}
                  component={r.component}
                />
              ))}
            </Switch>
          </SecondaryLayout>
        )}
      />

      <Route
        path={defaultSecondaryRoutes.map(r => r.path)}
        exact={true}
        render={() => (
          <SecondaryLayout>
            <Switch>
              {defaultSecondaryRoutes.map((r, i) => (
                <Route
                  exact={true}
                  key={i}
                  path={r.path}
                  component={r.component}
                />
              ))}
            </Switch>
          </SecondaryLayout>
        )}
      />

      <Route
        path={customRoutes.map(r => r.path)}
        exact={true}
        render={() => (
          <Switch>
            {customRoutes.map((r, i) => (
              <Route
                exact={true}
                key={i}
                path={r.path}
                component={r.component}
              />
            ))}
          </Switch>
        )}
      />
    </Switch>
  );
};

const App = () => {
  const [stripe, setStripe] = useState<stripe.Stripe | null>(null);
  const wStripe = isClient() ? window.Stripe : null;
  const { data } = useGetFeaturesQuery({});
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    rebootIntercom();
  }, []);

  useEffect(() => {
    page("Pageview");
  }, [location]);

  useEffect(() => {
    if (stripe || isServer()) {
      return;
    }
    setStripe(window.Stripe(config.STRIPE_PUBLISHABLE_KEY));
  }, [wStripe, stripe]);

  useEffect(() => {
    if (data && data.features) {
      dispatch(featuresActions.setFlags({ flags: data.features }));
    }
  }, [dispatch, data]);

  return (
    <SentryErrorBoundary>
      <Helmet>
        <title>LloydsDirect</title>
        <link
          rel="icon"
          type="image/png"
          href={require("../images/favicon-16.png")}
          sizes="16x16"
        />
        <link
          rel="icon"
          type="image/png"
          href={require("../images/favicon-32.png")}
          sizes="32x32"
        />
        <link
          rel="icon"
          type="image/png"
          href={require("../images/favicon-96.png")}
          sizes="96x96"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={require("../images/touch-icon-iphone-retina.png")}
        />
        <link
          rel="apple-touch-icon"
          sizes="167x167"
          href={require("../images/touch-icon-ipad-retina.png")}
        />
        <link
          href="https://fonts.googleapis.com/css?family=IBM+Plex+Sans:400,500"
          rel="stylesheet"
        />
        <link
          href="https://storage.googleapis.com/echo-webapp/fonts/cahuenga.css"
          rel="stylesheet"
        />
        <script
          type="text/javascript"
          src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
          async={true}
        />
      </Helmet>

      <StripeProvider stripe={stripe}>
        <AppWrapper>
          <ScrollToTop />
          <Switch>
            <Route
              exact={true}
              path={authenticatedRoutes.map(r => r.path)}
              render={() => (
                <AuthenticatedOnly>
                  <RoutesLayoutSwitch routes={authenticatedRoutes} />
                </AuthenticatedOnly>
              )}
            />
            <Route
              exact={true}
              path={publicRoutes.map(r => r.path)}
              render={() => <RoutesLayoutSwitch routes={publicRoutes} />}
            />
          </Switch>
        </AppWrapper>
      </StripeProvider>
    </SentryErrorBoundary>
  );
};

export default App;
